import React, { useState } from 'react';

import { Badge, Button, Menu, Tooltip, ChevronRight, DotsGrid, DotsVertical, PauseCircle, Plus } from '@opendate-app/setlist';

import { SALES_PAUSED_TITLE, SALES_PAUSED_DETAIL, SALES_PAUSED_DESCRIPTION } from '../utils/ticketTypeSalesPauseCopy';

import './TicketTypeRowV2.css';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export const toAmount = (value) => parseFloat(String(value).replace(/,/g, ''));

export const formatPrice = (priceValue) => (
  priceValue === 'FREE' ? 'FREE' : (priceValue == null ? '—' : currencyFormatter.format(toAmount(priceValue)))
);

const WINDOW_BADGES = {
  presale: { color: 'brand', label: 'Pre-sale' },
  public: { color: 'success', label: 'Public' },
  dayOf: { color: 'warning', label: 'Day of show' },
};

// The redesign ticket row, without the confirm's data or server actions, so
// the series wizard renders its ticket list in exactly the same markup.
export const TicketTypeRowView = ({
  sortableRef, style, dragHandleProps, draggable = true, last, name, paused, presale, subLine, salesWindowText,
  priceDisplay, inventory, actions, windows, free, basePrice, poolCount, soldPercent, poolStats, poolHint = 'Every window draws from this pool',
  onEditWindow,
}) => {
  const [expanded, setExpanded] = useState(true);
  const hasWindows = windows.length > 1;

  return (
    <div
      ref={sortableRef}
      style={style}
      className={`ticket-type-row-v2 ${last ? 'ticket-type-row-v2--last' : ''}`}
    >
      <div className="ticket-type-v2-grid-row ticket-type-row-v2-main">
        <div className="ticket-type-row-v2-cell ticket-type-row-v2-name-cell">
          <button
            type="button"
            className={`ticket-type-row-v2-caret ${hasWindows ? '' : 'ticket-type-row-v2-caret--hidden'} ${expanded ? 'ticket-type-row-v2-caret--open' : ''}`}
            aria-hidden={!hasWindows}
            tabIndex={hasWindows ? 0 : -1}
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse sales windows' : 'Expand sales windows'}
            onClick={() => hasWindows && setExpanded((prevExpanded) => !prevExpanded)}
          >
            <ChevronRight className="ticket-type-row-v2-caret-icon" aria-hidden="true" />
          </button>
          <a
            {...dragHandleProps}
            href="#"
            className="ticket-type-row-v2-drag-handle"
            style={draggable ? undefined : { visibility: 'hidden' }}
            onClick={(e) => e.preventDefault()}
            aria-label="Reorder ticket"
          >
            <DotsGrid width={16} height={16} aria-hidden="true" />
          </a>
          <div className="ticket-type-row-v2-name-text">
            <div className="ticket-type-row-v2-name-line">
              <div className="ticket-type-row-v2-name" title={name}>
                {name}
              </div>
              {paused ? (
                <Tooltip title={SALES_PAUSED_TITLE} supportingText={SALES_PAUSED_DETAIL} maxWidth={260}>
                  <span className="ticket-type-row-v2-paused-icon text-warning" role="img" aria-label={SALES_PAUSED_DESCRIPTION}>
                    <PauseCircle aria-hidden="true" />
                  </span>
                </Tooltip>
              ) : null}
            </div>
            {presale ? <Badge color="brand" size="sm">Pre-sale</Badge> : null}
            <div className="ticket-type-row-v2-subline">{subLine}</div>
          </div>
        </div>
        <div className="ticket-type-row-v2-cell">{salesWindowText}</div>
        <div className="ticket-type-row-v2-cell ticket-type-row-v2-price">{priceDisplay}</div>
        <div className="ticket-type-row-v2-cell">{inventory}</div>
        <div className="ticket-type-row-v2-cell ticket-type-row-v2-actions">
          <Menu
            placement="bottom-end"
            items={actions}
            trigger={(
              <Button
                variant="tertiary"
                size="sm"
                icon={DotsVertical}
                aria-label={`Actions for ${name}`}
              />
            )}
          />
        </div>
      </div>
      {expanded && hasWindows ? (
        <div className="ticket-type-row-v2-windows">
          <div className="ticket-type-row-v2-window-list">
            {windows.map((w) => (
              <button
                type="button"
                key={w.key}
                className="ticket-type-row-v2-window"
                aria-label={`Edit ${w.name}`}
                onClick={onEditWindow}
              >
                <span className={`ticket-type-row-v2-window-dot ticket-type-row-v2-window-dot--${w.kind}`} aria-hidden="true" />
                <span className="ticket-type-row-v2-window-info">
                  <span className="ticket-type-row-v2-window-title">
                    <span className="ticket-type-row-v2-window-name">{w.name}</span>
                    <Badge color={WINDOW_BADGES[w.kind].color} size="sm">{WINDOW_BADGES[w.kind].label}</Badge>
                  </span>
                  <span className="ticket-type-row-v2-window-sub">{w.sub}</span>
                </span>
                <span className="ticket-type-row-v2-window-status">{w.status}</span>
                <span className="ticket-type-row-v2-window-price">
                  {free ? 'FREE' : formatPrice(w.price)}
                  {!free && w.kind !== 'public' && !Number.isNaN(toAmount(w.price)) && toAmount(w.price) !== toAmount(basePrice) ? (
                    <Badge color="warning" size="sm">CUSTOM</Badge>
                  ) : null}
                </span>
                <ChevronRight className="ticket-type-row-v2-window-chevron" aria-hidden="true" />
              </button>
            ))}
            <div className="ticket-type-row-v2-window-add">
              <Button variant="link-color" size="sm" icon={Plus} onClick={onEditWindow}>
                Add a sales window
              </Button>
            </div>
          </div>
          <div className="ticket-type-row-v2-pool">
            <div className="ticket-type-row-v2-pool-label">Shared pool</div>
            <div className="ticket-type-row-v2-pool-count">{poolCount}</div>
            <div className="ticket-type-row-v2-pool-bar" role="presentation">
              <div className="ticket-type-row-v2-pool-bar-fill" style={{ width: `${soldPercent}%` }} />
            </div>
            {poolStats ? <div className="ticket-type-row-v2-pool-stats">{poolStats}</div> : null}
            <div className="ticket-type-row-v2-pool-hint">{poolHint}</div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export const TicketTypeTableHeader = () => (
  <div className="ticket-type-v2-grid-row ticket-type-v2-header-row">
    <div>Ticket Name</div>
    <div>Sales window</div>
    <div>Price</div>
    <div>Inventory</div>
    <div>&nbsp;</div>
  </div>
);

